import { faBarcodeRead, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { startOfDay } from 'date-fns';
import { useState } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useMatch,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { Filters } from 'src/shared/Filters/Filters';
import { LocationFilter } from 'src/shared/Filters/LocationFilter';
import { PermissionCheck } from 'src/shared/PermissionCheck';
import { WebviewBackButton } from 'src/shared/WebviewBackButton';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { Box } from '../../components/Box';
import { Chip } from '../../components/Chip';
import { Container } from '../../components/Container';
import { Icon } from '../../components/Icon';
import { IconButton } from '../../components/IconButton';
import { IconDatePicker } from '../../components/IconDatePicker';
import { MobileAppHeader } from '../../components/MobileAppHeader';
import { PageHeader } from '../../components/PageHeader';
import { Scroller } from '../../components/Scroller';
import { Stack } from '../../components/Stack';
import { useSession } from '../../contexts/SessionContext';
import { isTrainer } from '../../models/user/utils';

import AppointmentCheckinModal from './AppointmentCheckinModal';
import { EditAppointmentDialog } from './EditAppointmentDialog';

export function AppointmentsHeader({ hasDependents }) {
  const location = useLocation();

  const navigate = useNavigate();
  const match = useMatch('/appointments/:filter');
  const upcomingMatch = !match || match.params.filter == 'upcoming';
  const historyMatch = match?.params.filter == 'history';
  const canceledMatch = match?.params.filter == 'canceled';
  const [searchParams, setSearchParams] = useSearchParams();
  const allStaff = searchParams.get('allStaff');
  const allDependents = searchParams.get('allDependents') == 'true';
  const { user } = useSession();

  const {
    updateFilterParam,
    getFilterParamValue,
    resetFilterParams,
  } = useListSearchParams();

  const setDate = (date) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('date', startOfDay(date).toISOString());
    setSearchParams(newSearchParams);
  };

  const locationFilters = [
    {
      field: 'locationId',
      label: 'Location',
      Component: LocationFilter,
    },
  ];

  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [showNewEvent, setShowNewEvent] = useState(false);
  const [openCheckInModal, setOpenCheckInModal] = useState(false);

  const title = 'My Sessions';

  const actions = (
    <Stack direction="row" spacing={1}>
      <IconDatePicker onChange={(date) => setDate(date)} />
      {isTrainer(user) ? (
        <IconButton
          onClick={() => {
            setShowNewEvent(true);
          }}
        >
          <Icon size="medium" icon={faPlus} />
        </IconButton>
      ) : (
        <PermissionCheck can="globalCheckIn">
          <IconButton onClick={() => setOpenCheckInModal(true)}>
            <Icon
              size="medium"
              icon={faBarcodeRead}
            />
          </IconButton>
        </PermissionCheck>
      )}
    </Stack>
  );

  const filterChipsRow = (
    <Scroller direction="row" spacing={1} hideScrollbar>
      <Chip
        component={RouterLink}
        to={`/appointments/upcoming${location.search}`}
        active={upcomingMatch}
        variant="outlined"
        label="Upcoming"
        clickable
      />
      <Chip
        component={RouterLink}
        to={`/appointments/history${location.search}`}
        active={historyMatch}
        variant="outlined"
        label="Completed"
        clickable
      />

      {isTrainer(user) && (
        <>
          <Chip
            component={RouterLink}
            to={`/appointments/canceled${location.search}`}
            active={canceledMatch}
            variant="outlined"
            label="Canceled"
            clickable
          />
          <PermissionCheck can="viewOthersScheduling">
            <Chip
              onClick={() => {
                navigate('?allStaff=true');
              }}
              onDelete={
                allStaff
                  ? () => {
                      navigate('?allStaff=');
                    }
                  : undefined
              }
              active={!!allStaff}
              variant="outlined"
              label="All Staff"
              clickable
            />
          </PermissionCheck>
          <Filters
            filters={locationFilters}
            hideMainDropdown
            updateFilterParam={updateFilterParam}
            getFilterParamValue={getFilterParamValue}
            resetFilterParams={resetFilterParams}
          />
        </>
      )}

      {!isTrainer(user) && hasDependents && (
        <Chip
          onClick={() => {
            navigate('?allDependents=true');
          }}
          onDelete={
            allDependents
              ? () => {
                  navigate('?allDependents=false');
                }
              : undefined
          }
          active={allDependents}
          variant="outlined"
          label="Dependents"
          clickable
        />
      )}
    </Scroller>
  );

  return (
    <>
      {mdDown ? (
        <MobileAppHeader
          title={title}
          actions={actions}
          navigation={<WebviewBackButton />}
          row={
            <Box px={2} pt={0} pb={1}>
              {filterChipsRow}
            </Box>
          }
        />
      ) : (
        // <Container maxWidth="md" sx={{ pt: 4, pb: 2 }}>
        //   <Stack
        //     direction="row"
        //     justifyContent="space-between"
        //     alignItems="center"
        //   >
        //     <Typography variant="h4">{title}</Typography>
        //     <Box>{actions}</Box>
        //   </Stack>
        //   <Box mt={3}>{filterChipsRow}</Box>
        // </Container>
        (<Container maxWidth="md" sx={{ pt: 4, pb: 2 }}>
          <PageHeader title={title} actions={actions} />
          <Box mt={3}>{filterChipsRow}</Box>
        </Container>)
      )}
      <EditAppointmentDialog
        open={showNewEvent}
        onClose={() => setShowNewEvent(false)}
      />
      <PermissionCheck can="globalCheckIn">
        <AppointmentCheckinModal
          open={openCheckInModal}
          onHandleClose={() => setOpenCheckInModal(false)}
        />
      </PermissionCheck>
    </>
  );
}
